/**
 * DealProductsTab - Aba de produtos/serviços do deal
 * Interface limpa com modal de catálogo separado
 *
 * Versão: 3.1.0
 * - 3.1.0 (2026-08-12): quando o negócio possui proposta ACEITA, os itens ficam
 *   somente leitura (trava também garantida no banco por trigger).
 * - Refatorado para exibir apenas lista de itens
 * - Catálogo movido para modal dedicado
 * - Botão "Adicionar Produto" abre modal
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useCrmDealItems } from '@/hooks/useCrmDealItems';
import { useDealHasAcceptedProposal } from '@/hooks/useDealAcceptedProposal';
import { supabase } from '@/integrations/supabase/client';
import {
  DealItemsSummary,
  ProductCatalogModal,
} from './products';

// Props do componente
interface DealProductsTabProps {
  dealId: string;
  currency: string;
  readOnly: boolean;
  /** Indica se a aba está ativa (para lazy loading) */
  isActive?: boolean;
  /** ID do workspace do Deal para filtrar catálogo */
  workspaceId?: string | null;
}

/**
 * Aba de produtos/serviços com interface limpa
 * Catálogo acessível via modal dedicado
 */
export function DealProductsTab({ 
  dealId, 
  currency, 
  readOnly: readOnlyProp, 
  isActive = true,
  workspaceId 
}: DealProductsTabProps) {
  // Estado do modal de catálogo
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  // Estado para tabela de preço selecionada (persiste entre aberturas do modal)
  const [selectedPriceTableId, setSelectedPriceTableId] = useState<string | null>(null);

  // v3.1.0: proposta aceita congela os itens do negócio (integridade do snapshot)
  const { hasAcceptedProposal } = useDealHasAcceptedProposal(dealId, isActive);
  const readOnly = readOnlyProp || hasAcceptedProposal;


  // Buscar itens do deal (lazy loading)
  const { 
    items: dealItems, 
    isLoading: isLoadingItems,
    addItem,
    updateItem,
    removeItem,
  } = useCrmDealItems(dealId, { enabled: isActive });

  // Mapear itens do deal para o modal
  const dealItemsForModal = useMemo(() => {
    return dealItems.map((item) => ({
      product_id: item.product_id,
      quantity: item.quantity,
    }));
  }, [dealItems]);

  // v2.0.0 (2026-07-11): calcula IDs de produtos "órfãos" — presentes no deal
  // mas não vinculados à workspace atual. Aparece só após mover o deal entre
  // workspaces; usa a tabela ponte crm_product_workspaces.
  const productIds = useMemo(
    () => Array.from(new Set(dealItems.map((i) => i.product_id).filter(Boolean))),
    [dealItems]
  );
  const { data: orphanProductIds } = useQuery({
    queryKey: ['deal-orphan-products', workspaceId, productIds.sort().join(',')],
    enabled: isActive && !!workspaceId && productIds.length > 0,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_product_workspaces')
        .select('product_id')
        .eq('workspace_id', workspaceId as string)
        .in('product_id', productIds);
      if (error) throw error;
      const linked = new Set((data || []).map((r: any) => r.product_id));
      return new Set(productIds.filter((id) => !linked.has(id)));
    },
  });


  // Handler para abrir o catálogo
  const handleOpenCatalog = useCallback(() => {
    setCatalogModalOpen(true);
  }, []);

  // Handler para adicionar item (usado pelo modal)
  const handleAddItem = useCallback(
    async (productId: string, quantity: number, unitPrice: number) => {
      try {
        await addItem.mutateAsync({
          deal_id: dealId,
          product_id: productId,
          quantity,
          unit_price: unitPrice,
          price_table_id: selectedPriceTableId,
        });
      } catch (error) {
        // Erro já tratado pelo hook
      }
    },
    [addItem, dealId, selectedPriceTableId]
  );

  // Handler para atualizar quantidade via modal (busca pelo product_id)
  const handleUpdateQuantityByProduct = useCallback(
    async (productId: string, quantity: number) => {
      const item = dealItems.find((i) => i.product_id === productId);
      if (item) {
        try {
          await updateItem.mutateAsync({
            id: item.id,
            quantity,
          });
        } catch (error) {
          // Erro já tratado pelo hook
        }
      }
    },
    [dealItems, updateItem]
  );

  // Handler para remover item via modal (busca pelo product_id)
  const handleRemoveByProduct = useCallback(
    async (productId: string) => {
      const item = dealItems.find((i) => i.product_id === productId);
      if (item) {
        try {
          await removeItem.mutateAsync(item.id);
        } catch (error) {
          // Erro já tratado pelo hook
        }
      }
    },
    [dealItems, removeItem]
  );

  // Handlers para tabela de itens (usa item.id diretamente)
  const handleUpdateQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      try {
        await updateItem.mutateAsync({ id: itemId, quantity });
      } catch (error) {
        // Erro já tratado pelo hook
      }
    },
    [updateItem]
  );

  // Handler para atualizar desconto (suporta % e valor fixo)
  const handleUpdateDiscount = useCallback(
    async (itemId: string, percent: number, fixed: number) => {
      try {
        await updateItem.mutateAsync({ 
          id: itemId, 
          discount_percent: percent, 
          discount_fixed: fixed,
        });
      } catch (error) {
        // Erro já tratado pelo hook
      }
    },
    [updateItem]
  );

  const handleRemoveItem = useCallback(
    async (itemId: string) => {
      try {
        await removeItem.mutateAsync(itemId);
      } catch (error) {
        // Erro já tratado pelo hook
      }
    },
    [removeItem]
  );

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Aviso: proposta aceita congela produtos/serviços */}
      {hasAcceptedProposal && (
        <InlineNotice variant="warning">
          Este negócio possui uma proposta aceita. Para alterar os produtos e serviços, cancele
          antes a proposta aceita.
        </InlineNotice>
      )}

      {/* Botão Adicionar Produto (apenas quando não readOnly) */}
      {!readOnly && (
        <Button 
          variant="outline" 
          className="w-full h-12 border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-colors"
          onClick={handleOpenCatalog}
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Produto ou Serviço
        </Button>
      )}

      {/* Resumo dos Itens do Deal */}
      <DealItemsSummary
        items={dealItems}
        currency={currency}
        readOnly={readOnly}
        onUpdateQuantity={handleUpdateQuantity}
        onUpdateDiscount={handleUpdateDiscount}
        onRemove={handleRemoveItem}
        onOpenCatalog={!readOnly ? handleOpenCatalog : undefined}
        orphanProductIds={orphanProductIds}
      />

      {/* Modal de Catálogo de Produtos */}
      <ProductCatalogModal
        open={catalogModalOpen}
        onOpenChange={setCatalogModalOpen}
        dealId={dealId}
        currency={currency}
        workspaceId={workspaceId}
        dealItems={dealItemsForModal}
        onAddItem={handleAddItem}
        onUpdateItemQuantity={handleUpdateQuantityByProduct}
        onRemoveItem={handleRemoveByProduct}
      />
    </div>
  );
}
